import { useEffect } from 'react';
import { create } from 'zustand';
import { X, CheckCircle, AlertCircle, Loader2, Copy, Share2, Coins } from 'lucide-react';
import { useAppKitAccount } from '@reown/appkit/react';
import { useWallet } from '@solana/wallet-adapter-react';

// Define transaction status types
export type TransactionStatus = 'pending' | 'success' | 'error';

// Popup display time in milliseconds (15 seconds)
const POPUP_DISPLAY_TIME = 15000;

// Interface for the transaction state
interface TransactionState {
  isOpen: boolean;
  status: TransactionStatus;
  message: string;
  hash?: string;
  network: 'eth' | 'bsc' | 'sol';
  txUrl?: string;
  tokensReceived?: string;
  isApproval: boolean;
  onBuyClick: (() => void) | null;
  setOpen: (open: boolean) => void;
  showTransaction: (
    status: TransactionStatus, 
    message: string, 
    network: 'eth' | 'bsc' | 'sol', 
    hash?: string,
    tokensReceived?: string,
    isApproval?: boolean,
    onBuyClick?: (() => void) | null
  ) => void;
  closeTransaction: () => void;
}

// Create zustand store for managing transaction state
export const useTransactionPopup = create<TransactionState>((set) => ({
  isOpen: false,
  status: 'pending',
  message: '',
  hash: undefined,
  network: 'eth',
  txUrl: undefined,
  tokensReceived: undefined,
  isApproval: false,
  onBuyClick: null,
  setOpen: (open) => set({ isOpen: open }),
  showTransaction: (status, message, network, hash, tokensReceived, isApproval = false, onBuyClick = null) => {
    let txUrl: string | undefined;
    
    if (hash) {
      // Set explorer URL based on network
      if (network === 'eth') {
        txUrl = `https://etherscan.io/tx/${hash}`;
      } else if (network === 'bsc') {
        txUrl = `https://bscscan.com/tx/${hash}`;
      } else if (network === 'sol') {
        txUrl = `https://solscan.io/tx/${hash}`;
      }
    }
    
    set((state) => {
      // If the same hash and just updating status, make sure to preserve all other properties
      if (state.hash && state.hash === hash && state.status === 'pending' && status !== 'pending') {
        // Same transaction, status changed - update relevant fields only
        return { 
          status, 
          message, 
          isOpen: true,
          tokensReceived: tokensReceived || state.tokensReceived 
        };
      } else {
        // Completely new transaction or first display of this transaction
        return { 
          isOpen: true, 
          status, 
          message, 
          hash, 
          network, 
          txUrl, 
          tokensReceived, 
          isApproval, 
          onBuyClick 
        };
      }
    });
  },
  closeTransaction: () => set({ isOpen: false }),
}));

// Main component
export default function TransactionPopup() {
  const { isOpen, status, message, txUrl, hash, tokensReceived, closeTransaction, network, isApproval, onBuyClick } = useTransactionPopup();
  
  // Get the connected wallet addresses
  const { address: evmAddress, isConnected: isEvmConnected } = useAppKitAccount();
  const { publicKey: solanaAddress, connected: isSolanaConnected } = useWallet();

  // Get referral link using the appropriate connected wallet address
  const getReferralLink = () => {
    let address;
    
    // Use the appropriate address based on connected wallet type
    if (network === 'sol' && isSolanaConnected && solanaAddress) {
      address = solanaAddress.toBase58();
    } else if ((network === 'eth' || network === 'bsc') && isEvmConnected && evmAddress) {
      address = evmAddress;
    } else {
      // Fallback to localStorage or default if no wallet is connected
      address = localStorage.getItem('walletAddress') || '0x0000000000000000000000000000000000000000';
    }
    
    // Use the full address for proper database matching
    const baseUrl = window.location.origin;
    return `${baseUrl}/?ref=${address}`;
  };

  // Function to copy text to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // Could add a toast notification here if available in the project
  };

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeTransaction();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [closeTransaction]);
  
  // Auto-close successful transactions after delay, but not for approval with Buy button
  // Disabled per user request - keep popups open until manually closed
  /* 
  useEffect(() => {
    // Only auto-close if it's not an approval transaction with Buy button
    if (isOpen && status === 'success' && !(isApproval && onBuyClick)) {
      const timer = setTimeout(() => {
        closeTransaction();
      }, 20000); // Close after 20 seconds for other successful transactions
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, status, closeTransaction, isApproval, onBuyClick]);
  */

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto"
      onClick={(e) => {
        // Close when clicking on the overlay background (but not when clicking on the popup content)
        if (e.target === e.currentTarget) {
          closeTransaction();
        }
      }}
    >
      <div className="relative w-full max-w-md mx-auto">
        <div className="quantum-card p-6 border border-quantum-primary/30 shadow-lg shadow-quantum-primary/20">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold bg-gradient-to-r from-quantum-primary to-quantum-secondary bg-clip-text text-transparent">
              {status === 'pending' ? 'Processing Transaction' : 
               status === 'success' ? 'Transaction Successful' : 'Transaction Failed'}
            </h3>
            <button
              onClick={closeTransaction}
              className="text-quantum-light/70 hover:text-quantum-light focus:outline-none"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="flex items-center mb-4">
            {status === 'pending' && (
              <div className="mr-3 text-quantum-primary">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            )}
            {status === 'success' && (
              <div className="mr-3 text-green-500">
                <CheckCircle className="w-8 h-8" />
              </div>
            )}
            {status === 'error' && (
              <div className="mr-3 text-red-500">
                <AlertCircle className="w-8 h-8" />
              </div>
            )}
            
            <p className="text-quantum-light/90 flex-1">
              {message}
            </p>
          </div>
          
          {/* Add buy button if this was an approval transaction and succeeded */}
          {status === 'success' && isApproval && onBuyClick && (
            <div className="mt-4 mb-4 flex justify-center">
              <button 
                onClick={() => {
                  closeTransaction(); // Close popup first
                  if (onBuyClick) onBuyClick(); // Then trigger the buy function
                }}
                className="quantum-button py-2 px-6 flex items-center justify-center gap-2"
              >
                <Coins className="w-4 h-4"/>Buy Now
              </button>
            </div>
          )}
          
          {/* Token display section for successful purchases */}
          {status === 'success' && tokensReceived && (
            <div className="mt-3 mb-4 p-3 bg-quantum-dark/50 rounded-lg border border-quantum-primary/20">
              <p className="text-quantum-light mb-1 font-medium">Tokens received:</p>
              <p className="text-quantum-primary text-xl font-bold">{tokensReceived} QGEN</p>
            </div>
          )}

          {/* Only show referral section if it's not an approval transaction */}
          {status === 'success' && !isApproval && (
            <div className="mt-3 mb-4 p-3 bg-quantum-dark/50 rounded-lg border border-quantum-primary/20">
              <p className="text-quantum-light mb-2 font-medium">Share your referral link:</p>
              <div className="flex items-center justify-between bg-quantum-dark rounded p-2 mb-2">
                <div className="truncate text-xs text-quantum-light/70 mr-2">
                  {(() => {
                    const fullLink = getReferralLink();
                    const addressPart = fullLink.split('ref=')[1];
                    return `${window.location.origin}/?ref=${addressPart.substring(0, 6)}...${addressPart.substring(addressPart.length - 4)}`;
                  })()}
                </div>
                <div className="flex">
                  <button 
                    onClick={() => copyToClipboard(getReferralLink())}
                    className="p-1 text-quantum-primary hover:text-quantum-secondary"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <a 
                    href={`https://twitter.com/intent/tweet?text=Join%20me%20in%20the%20Quantum%20presale%20with%20my%20referral%20link!%20${encodeURIComponent(getReferralLink())}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 text-quantum-primary hover:text-quantum-secondary"
                  >
                    <Share2 className="w-4 h-4" />
                  </a>
                </div>
              </div>
              <p className="text-xs text-quantum-light/70">Earn 5% of referred purchases</p>
            </div>
          )}
          
          {txUrl && (
            <div className="mt-4 flex justify-between items-center">
              <a 
                href={txUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-sm text-quantum-primary hover:text-quantum-secondary transition-colors flex items-center"
              >
                View on explorer
                <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
              
              {hash && (
                <span className="text-xs text-quantum-light/50 truncate max-w-[150px]">
                  TX: {hash.slice(0, 6)}...{hash.slice(-4)}
                </span>
              )}
            </div>
          )}

          {/* Add close button for success/error states */}
          {(status === 'success' || status === 'error') && (
            <div className="mt-6 text-center">
              <button 
                onClick={closeTransaction}
                className="px-6 py-2 bg-gradient-to-r from-quantum-primary to-quantum-secondary text-quantum-dark rounded-lg hover:opacity-90 transition-all font-medium w-full"
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 